import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal, Typography } from "djuno-design";
import { AiAppScalingSchema } from "../../../../utils/validations";
import {
  handleToggleAiAppScalingModal,
  selectAiAppsActionLoading,
  selectAiAppScalingEditor,
  selectSelectedAiApp,
  updateAiAppScalingAsync,
} from "../../../../store/ai/deploys/appsSlice";
import { AiAppScallingStrategy } from "../../../../types/ai";
import { getAiAppAsync } from "../../../../store/ai/deploys/appSlice";
import { AiAppScallingStrategyInput } from "../../create/components";
import { currency } from "../../../../utils/currency";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "../../../../store/database/servicesSlice";

const AiAppScalingModal = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectAiAppScalingEditor);
  const loading = useAppSelector(selectAiAppsActionLoading);

  const selectedApp = useAppSelector(selectSelectedAiApp);

  const [scallingStrategy, setScallingStrategy] =
    useState<AiAppScallingStrategy>("fixed");

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const {
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(AiAppScalingSchema(scallingStrategy)),
    shouldUnregister: true,
    reValidateMode: "onChange",
    defaultValues: {
      replicas: 1,
      averageUsageTarget: 75,
      replicasMax: 1,
      replicasMin: 1,
      resourceType: "CPU",
    },
  });

  const replicas = watch("replicas");
  const replicasMin = watch("replicasMin");
  const replicasMax = watch("replicasMax");
  const resourceType = watch("resourceType");
  const averageUsageTarget = watch("averageUsageTarget");

  useEffect(() => {
    if (selectedApp && isOpen) {
      const _sterategy: AiAppScallingStrategy = selectedApp.spec.scalingStrategy
        ?.fixed
        ? "fixed"
        : "automatic";
      setScallingStrategy(_sterategy);
      if (_sterategy === "fixed") {
        setValue(
          "replicas",
          selectedApp.spec.scalingStrategy?.fixed?.replicas || 1
        );
      }
      if (_sterategy === "automatic") {
        setValue(
          "replicasMin",
          selectedApp.spec.scalingStrategy?.automatic?.replicasMin || 1
        );
        setValue(
          "replicasMax",
          selectedApp.spec.scalingStrategy?.automatic?.replicasMax || 1
        );
        setValue(
          "averageUsageTarget",
          selectedApp.spec.scalingStrategy?.automatic?.averageUsageTarget || 75
        );
        setValue(
          "resourceType",
          selectedApp.spec.scalingStrategy?.automatic?.resourceType || "cpu"
        );
      }
    }
  }, [selectedApp, isOpen]);

  //get first data
  useEffect(() => {
    dispatch(getDBSCatalogsAsync("ai-app"));
  }, [dispatch]);

  const catalog = useMemo(() => {
    if (!selectedApp && !catalogs) return undefined;
    return catalogs?.find((c) =>
      c.planCode.startsWith(
        "ai-app." + selectedApp?.spec.resources.flavor + ".minute.consumption"
      )
    );
  }, [catalogs]);

  const onSubmit = () => {
    if (!loading && selectedApp) {
      let scaling: any = {};
      if (scallingStrategy === "fixed") {
        scaling = {
          fixed: { replicas },
        };
      }
      if (scallingStrategy === "automatic") {
        scaling = {
          automatic: {
            averageUsageTarget,
            replicasMax,
            replicasMin,
            resourceType,
          },
        };
      }

      dispatch(
        updateAiAppScalingAsync({
          id: selectedApp.id,
          data: scaling,
        })
      ).then((action) => {
        if (action.type === "ai-apps/update/scaling/fulfilled") {
          handleOnClose();
          dispatch(
            getAiAppAsync({
              id: selectedApp.id,
            })
          );
        }
      });
    }
  };

  const handleOnClose = useCallback(() => {
    reset();
    dispatch(handleToggleAiAppScalingModal({ isOpen: false, app: null }));
  }, [dispatch, reset]);

  useEffect(() => {
    return () => {
      handleOnClose();
    };
  }, [handleOnClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} contentClassName="max-w-lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <AiAppScallingStrategyInput
          control={control}
          scallingStrategy={scallingStrategy}
          setScallingStrategy={setScallingStrategy}
          errors={errors}
          loading={catalogsLoading}
        />

        {catalog && selectedApp && (
          <Typography.Text className="!text-sm">
            Price for selected resources:{" "}
            {currency(
              (selectedApp?.spec.resources.cpu ||
                selectedApp?.spec.resources.gpu) *
                catalog.pricings[0].price *
                60 *
                (scallingStrategy === "fixed"
                  ? replicas || 1
                  : replicasMin || 1),
              {
                minimumFractionDigits: 3,
              }
            )}
            /hour
          </Typography.Text>
        )}

        {/* <Alert uiType="info" showIcon>
          <Typography.Text size="xs">
            The price displayed above applies to the physical resources for
            deployment. Additional fees may apply depending on the partner model
            chosen. Please refer to the summary.
          </Typography.Text>
        </Alert> */}

        <div className="mt-4 flex justify-end gap-2">
          <Button uiType="light" onClick={handleOnClose}>
            Cancel
          </Button>

          <Button uiType="primary" type="submit" loading={loading}>
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AiAppScalingModal;
